<script setup lang="ts">
import { computed, ref } from 'vue'
import { Cropper, CircleStencil } from 'vue-advanced-cropper'
import 'vue-advanced-cropper/dist/style.css'

export interface Props {
  src: string | null
  size?: number
  tooltip?: string
}

const props = withDefaults(defineProps<Props>(), {
  size: 10,
  tooltip: 'Klicken um Profilbild oder Logo hochzuladen'
})

const cropperRef = ref()

const sizeClass = computed(() => {
  return {
    4: 'h-4 w-4',
    5: 'h-5 w-5',
    6: 'h-6 w-6',
    10: 'h-10 w-10',
    12: 'h-12 w-12',
    14: 'h-14 w-14',
    16: 'h-16 w-16'
  }[props.size]
})

const emit = defineEmits(['avatarChanged'])

const src = ref(props.src)
const open = ref(false)
const pic = ref()

const blobToBase64 = async (blob) => {
  return new Promise((resolve) => {
    const reader = new FileReader()
    reader.onloadend = () => resolve(reader.result)
    reader.readAsDataURL(blob)
  })
}

const onInputChange = async (event: Event) => {
  const file: File = (event.target as HTMLInputElement).files[0]

  const reader = new FileReader()
  reader.readAsArrayBuffer(file)

  reader.onload = async () => {
    const arrayBuffer = reader.result as ArrayBuffer
    const blob = new Blob([arrayBuffer], { type: file.type })
    pic.value = await blobToBase64(blob)
    open.value = true
  }
}

const onRemoveAvatar = () => {
  src.value = null
  emit('avatarChanged', null)
}

const onClose = () => {
  open.value = false
  pic.value = ''
}

const onSave = () => {
  const { canvas } = cropperRef.value.getResult()
  src.value = canvas.toDataURL()
  emit('avatarChanged', src.value)
  open.value = false
}

</script>

<template>
  <label
    v-tooltip="'Profilbild oder Logo hochladen'"
    for="file-input"
    class="cursor-pointer"
  >

    <input
      id="file-input"
      class="w-0 h-0"
      type="file"
      @change="onInputChange"
    >

    <div
      v-if="!src"
      class="flex items-center flex-1 -mt-3 border border-stone-200 rounded-full relative"
    >
      <span
        class="inline-block mx-auto rounded-full border-white border-2 overflow-hidden bg-stone-100 text-stone-300"
      >
        <svg
          :class="[sizeClass]"
          fill="currentColor"
          viewBox="0 0 24 24"
        >
          <path d="M24 20.993V24H0v-2.996A14.977 14.977 0 0112.004 15c4.904 0 9.26 2.354 11.996 5.993zM16.002 8.999a4 4 0 11-8 0 4 4 0 018 0z" />
        </svg>
      </span>
    </div>

    <div
      v-else
      class="flex items-center flex-1 -mt-3 border border-stone-200 rounded-full relative"
    >
      <span
        class="inline-block mx-auto rounded-full border-white border-2 overflow-hidden bg-stone-100 text-stone-300"
        @change="onInputChange"
      >

        <img
          :src="src"
          class="mx-auto"
          :class="[sizeClass]"
        >
        <span class="absolute bottom-0 right-0  rounded-full ring ring-white bg-white">
          <storm-ui-icon
            name="tabler-trash"
            class="size-5 text-gray-400 hover:text-red-500"
            @click.stop.prevent="onRemoveAvatar"
          />
        </span>
      </span>
    </div>
    <storm-ui-dialog
      :show="open"
      height="auto"
      width="md"
      title="Bild zuschneiden"
      @hide="onClose"
    >
      <template #content>
        <Cropper
          ref="cropperRef"
          :src="pic"
          :stencil-component="CircleStencil"
          :stencil-props="{
            aspectRatio: 1/1,
          }"
          :canvas="{
            height: 256,
            width: 256
          }"
        />
      </template>
      <template #footer>
        <div class="flex items-center justify-end space-x-2">
          <storm-ui-button
            type="button"
            variant="link"
            @click="onClose"
          >
            Abbrechen
          </storm-ui-button>
          <storm-ui-button
            type="button"
            variant="primary"
            @click="onSave"
          >
            Speichern
          </storm-ui-button>
        </div>
      </template>
    </storm-ui-dialog>
  </label>
</template>
